import { portalTelemetryMiddleware } from '../telemetry/portalTelemetryMiddleware';
import { combineReducers, configureStore } from "@reduxjs/toolkit";
import type { UnknownAction } from '@reduxjs/toolkit';
import { createApiErrorMiddleware } from './apiErrorMiddleware';
import { reportApiError } from '../layout/reportApiError';
import { sessionChanged, sessionReducer, sessionRequestsMiddleware } from './session';
import { ApiRequestStatus } from '../enums/apiRequestStatus';
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { creditsSlice, getDocumentAsync } from "../slices/creditsSlice";
import { referenceDataSlice } from "../slices/referenceDataSlice";
import { personSlice, downloadFileAsync } from "../slices/personSlice";
import { pcsPdpSlice } from "../slices/pcsPdpSlice";
import { alumniSlice } from "../slices/alumniSlice";
import { affiliateOnboardingSlice } from "../slices/affiliateOnboardingSlice";
import { famTransferSlice, famTransferDownloadFileAsync } from "../slices/famTransferSlice";
import { famOnboardingSlice } from "../slices/famOnboardingSlice";
import { volunteeringSlice } from "../slices/volunteeringSlice";
import { famSlice } from "../slices/famSlice";
import { paymentSlice } from "../slices/paymentSlice";
import { famEnrolmentSlice } from "../slices/famEnrolmentSlice";
import { donationsSlice } from "../slices/donationsSlice";
import { requestSlice } from "../slices/requestSlice";
import { directDebitMandateSlice } from "../slices/directDebitMandateSlice";
import { cardSubscriptionSlice } from "../slices/cardSubscriptionSlice";
import { workpostSlice } from "../slices/workpostSlice";
import { pcsEnrolmentSlice } from "../slices/pcsEnrolmentSlice";
import { pcsSlice } from "../slices/pcsSlice";
import pmdSlice from "../slices/pmdSlice";
import { shoppingBasketSlice } from "../slices/shoppingBasketSlice";
import { pcsEventSlice, getPcsEventDocumentAsync } from "../slices/pcsEventSlice";
import { calendarSlice } from "../slices/calendarSlice";
import { invalidateReadRequests } from './createReadAsyncThunk';

const combinedReducer = combineReducers({
        session: sessionReducer,
        affiliateOnboardingDetails: affiliateOnboardingSlice.reducer,
        alumniDetails: alumniSlice.reducer,
        cardSubscription: cardSubscriptionSlice.reducer,
        credits: creditsSlice.reducer,
        directDebit: directDebitMandateSlice.reducer,
        donations: donationsSlice.reducer,

        famDetails: famSlice.reducer,
        famEnrolment: famEnrolmentSlice.reducer,
        famOnboarding: famOnboardingSlice.reducer,
        famTransfer: famTransferSlice.reducer,
        payment: paymentSlice.reducer,
        pcsPdp: pcsPdpSlice.reducer,
        pcs: pcsSlice.reducer,
        calendar: calendarSlice.reducer,
        pcsEvent: pcsEventSlice.reducer,
        pcsEnrolment: pcsEnrolmentSlice.reducer,
        personalDetail: personSlice.reducer,
        pmd: pmdSlice.reducer,
        ReferenceData: referenceDataSlice.reducer,
        requests: requestSlice.reducer,
        shoppingBasket: shoppingBasketSlice.reducer,
        volunteeringInterests: volunteeringSlice.reducer,
        workpost: workpostSlice.reducer
});

export const rootReducer = (state: ReturnType<typeof combinedReducer> | undefined, action: UnknownAction) => {
    if (sessionChanged.match(action) && (!state?.session.initialized || state.session.identity !== action.payload)) {
        const initial = combinedReducer(undefined, action);
        return { ...initial, ReferenceData: state?.ReferenceData.staticDataStateStatus === ApiRequestStatus.Fulfilled
            ? state.ReferenceData : initial.ReferenceData };
    }
    return combinedReducer(state, action);
};

export const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({
        serializableCheck: {
            // Downloads return Blob payloads to callers without storing them in Redux.
            ignoredActions: [getDocumentAsync.fulfilled.type, downloadFileAsync.fulfilled.type,
                famTransferDownloadFileAsync.fulfilled.type, getPcsEventDocumentAsync.fulfilled.type]
        },
    }).prepend(sessionRequestsMiddleware).concat(invalidateReadRequests, createApiErrorMiddleware(reportApiError), portalTelemetryMiddleware)
})

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
